import Link from 'next/link'
import OpenGraph from 'components/OpenGraph'
import config from 'common/config'
import { useToggle } from 'common/hooks'
import emails from 'common/config/emails'
import Obfuscate from 'react-obfuscate'

const Privacy = (props) => {
  const [showDetails, toggleDetails] = useToggle(false)

  return (
    <div className="prose content bg-white bg-opacity-80 px-5 py-1 mb-5 rounded">
      <OpenGraph
        title="Privacy Policy"
        // TODO:
        // description=""
      />
      <div className="policy">
        <p>
          This privacy policy (&ldquo;Privacy Policy&rdquo;) explains how{' '}
          {config.companyName} (&ldquo;{config.appName}&rdquo; or
          &ldquo;we&rdquo;) collects and processes information from members and
          users of the website, software applications, and other online services
          we provide (collectively &ldquo;
          {config.appName}’s products&rdquo;). {config.appName}’s products
          include, but are not limited to, the {config.appName}
          extension for web browsers (the &ldquo;Extension&rdquo;), the{' '}
          {config.appName} mobile application (the &ldquo;Mobile App&rdquo;),
          and the website located at {config.siteRootUrl} (the
          &ldquo;Website&rdquo;).
        </p>
        <p>
          If we ever need additional personal information for a specific reason,
          we will tell you and ask for your permission. Also, to the extent that{' '}
          {config.appName} has links to other websites, please note that we do
          not own, operate, or control them, and they have their own controlling
          privacy policies that you should review.
        </p>

        <h3>What data we collect and why</h3>
        <p>
          In order to provide our products, {config.appName} collects
          information that you share directly with us when you use our Website,
          Extension, or Mobile App. {config.appName} also collects technical
          information about your device and use of {config.appName} to make sure
          that our products are working correctly. The information we collect is
          geared to providing {config.appName} members with a better way to
          browse online and to help us develop, improve, and market our
          services.
        </p>

        <h3>Account and Profile Information</h3>
        <p>
          When you become a member of {config.appName}, you share a limited
          amount of information during the registration process, optionally
          including your name, email address, member password and other
          registration information to personalize your profile. If you choose to
          link a social media profile (like Facebook) at sign-in,{' '}
          {config.appName} will store this information for login purposes.
          Linking your social media profile grants {config.appName}
          access to the profile, but only as allowed by your permission
          settings, that platform’s terms of service, and this Privacy Policy.
          We will NOT track any of your social media activity, and we cannot
          post anything on your behalf.
        </p>

        <h3>Technical Information.</h3>
        <p>
          {config.appName} automatically collects information to ensure that our
          products work correctly on different devices and browsers. This
          includes data like:
        </p>
        <ul>
          <li>the type of device you’re using</li>
          <li>the device’s unique ID (where available)</li>
          <li>operating system</li>
          <li>browser type</li>
          <li>IP address</li>
          <li>event stamp</li>
          <li>error logs</li>
        </ul>
        <p>
          This data allows us to make sure {config.appName} products are working
          correctly, to make your experience better, and to detect and prevent
          fraud. Also, to help us coordinate our communications and marketing
          campaigns, and to understand how our users engage with our products,
          we use common digital tools that allow us to see if you opened an
          email we sent, visited a link in those emails, or generally interacted
          with {config.appName} as a result of our marketing efforts.
        </p>
        <p>
          {config.appName} does not track your search engine history, emails, or
          your browsing on any site where you have not left a review. When you
          opt to leave a review, we collect the URL to attach to the review.
          Please note that users have the option to specify which parts of the
          URL are collected.
        </p>

        <h3>Aggregate and Anonymized Data</h3>
        <p>
          We may also use the information we collect in aggregate or otherwise
          anonymized form. This lets us look at interactions with our site and
          products generally and does not identify you or any specific person.
          We use this general data for research, development, marketing,
          analytics, and to enhance the experience for the {config.appName}{' '}
          community.
        </p>

        <h3>
          What data we do <em>not</em> collect
        </h3>
        <p>
          We collect information that is necessary for the operation of our
          network and no more. This does not include, and we do not collect, any
          information from your search engine history, emails, or from
          non-reviewed websites. We also do not collect bank or credit card
          information.
        </p>

        <h3>How we share your data</h3>
        <p>
          We know how important your personal data is to you, so we make sure
          you have all the control. We’ll only share it with your consent or
          where we are legally required to do so. We may share information with
          our service providers (businesses that work for and with us) for the
          purpose of running {config.appName} and fulfilling our commitments to
          you. These include businesses that perform services on our behalf,
          including to help us maintain our products, to provide support for
          legal, banking, security protection, payment processing, our own
          marketing, and customer service. Please know that when we share
          information with our service providers, it’s for the purposes outlined
          in this Privacy Policy and not for their independent use. We may also
          share information in the following cases:
        </p>
        <ul>
          <li>with your express consent;</li>
          <li>
            as required by law, or to comply or respond to a valid government
            request;
          </li>
          <li>
            when we believe in good faith that it’s necessary to protect our
            rights, protect your safety or the safety of others, or investigate
            fraud; and
          </li>
          <li>
            with a buyer or successor if {config.appName} is involved in a
            merger, acquisition, or similar corporate transaction. If that
            happens, you will be notified via email and/or a prominent notice on
            the Website of any change in ownership, as well as any choices you
            will have as a result.
          </li>
        </ul>

        <h3>How we protect your data</h3>
        <p>
          The security of your information is important to us. We are dedicated
          to protecting your information and have put in place industry standard
          electronic and procedural safeguards. These measures include limiting
          access, using encryption, employing pseudonymization and anonymization
          techniques, and more. Though we hate to say it, despite our efforts,
          we can’t guarantee that user information will not be accessed, viewed,
          disclosed, altered, or destroyed as a result of a breach of any of our
          safeguards; you provide us with information at your own risk.
        </p>

        <h3>How we use cookies and similar technologies</h3>
        <p>
          Like most other online services, {config.appName} uses cookies, pixel
          tags, web beacons, and other markers (we’ll just call them
          &ldquo;cookies&rdquo;) to collect some of the data discussed in this
          Privacy Policy. These help us operate our products and analyze
          engagement and usage. See below to learn how you can manage your
          cookie preferences. Some cookies can be temporary (&ldquo;session
          cookies&rdquo; like those used for navigating your browser) and
          disappear once you close it. Others last longer (&ldquo;persistent
          cookies,&rdquo; like when you ask a site to remember your login) and
          are saved to your computer until you delete them. Our uses of cookies
          and similar technologies fall into the following general categories:
        </p>
        <ul>
          <li>
            Essential: We may use cookies, web beacons, or other similar
            technologies that are necessary to the operation of our sites,
            services, applications, and tools. This includes technologies that
            allow you access to our sites, services, applications, and tools;
            that are required to identify irregular site behavior, prevent
            fraudulent activity and improve security; or that allow you to make
            use of our functionality.
          </li>
          <li>
            Performance: We may use cookies, web beacons, or other similar
            technologies to assess the performance of our websites,
            applications, services, and tools, including as part of our analytic
            practices to help us understand how our visitors use our websites,
            determine if you have interacted with our messaging, determine
            whether you have viewed an item or link, or to improve our website
            content, applications, services, or tools;
          </li>
          <li>
            Functionality: We may use cookies, web beacons, or other similar
            technologies that allow us to offer you enhanced functionality when
            accessing or using our sites, services, applications, or tools. This
            may include identifying you when you sign into our sites or keeping
            track of your specified preferences, interests, or past items viewed
            so that we may enhance the presentation of content on our sites;
          </li>
        </ul>
        <p>
          If you want to disable cookies entirely, your browser or mobile device
          might have an option to do that. For more information, including
          instructions on disabling cookies, please visit:{' '}
          <a
            href="http://www.allaboutcookies.org/"
            rel="nofollow noreferrer"
            target="_blank"
          >
            allaboutcookies.org
          </a>
          .
        </p>

        <h3>Your choices for managing your data</h3>
        <p>
          We’ve made it easy to opt out of {config.appName}’s products at any
          time. You can always adjust your personal settings regarding what
          information you share and how {config.appName} communicates with you
          by going to your account settings page. If you want to stop using{' '}
          {config.appName}, we have instructions on how to uninstall the
          Extension <Link href="/about/uninstalling">on our Website</Link> and
          if you have any issues you can contact our support team for assistance
          by sending an email to <Obfuscate email={emails.support} />.
        </p>
        <p>
          If you would like to delete your profile or personal information, you
          can always ask us by emailing <Obfuscate email={emails.privacy} />. We
          will delete any personal information we have, although the following
          exceptions apply: (1) we may still store your personal information if
          we need it to meet our legal or compliance obligations and/or to
          defend against legal claims; and (2) we may also continue to store
          your personal data in an aggregated and anonymized format that doesn’t
          identify you and can’t be attributed to you, even after you’ve deleted
          your profile.
        </p>

        <h3>Additional rights regarding your data</h3>
        <p>
          Depending on where you live, you may have additional rights as to the
          personal information that you share and {config.appName} collects.
          Residents of the EU, UK, Canada, Australia, New Zealand, and
          California can{' '}
          <a href="#" onClick={toggleDetails}>
            {showDetails ? 'read more below' : 'click here to learn more'}
          </a>
          .
        </p>
        {showDetails ? (
          <div id="geobased" className="bg-gray-100 px-3 py-0.5 rounded">
            <h3>EU, UK, Canada, Australia, and New Zealand residents</h3>
            <p>
              Controller: {config.appName} is a private company, established in
              the United States of America. Our address is{' '}
              {config.companyAddress}. Our contact email address is{' '}
              <Obfuscate email={emails.privacy} />. For the purposes of the
              General Data Protection Regulation, we are the data controller.
            </p>
            <p>
              Legal Bases: The personal information that you share and we
              collect, as well as its purposes, are described in this Privacy
              Policy. {config.appName}
              will only collect or process your personal information if we have
              a legal basis to do so. These are:
            </p>
            <ul>
              <li>
                When you consent to our use of your data for a specific purpose.
              </li>
              <li>
                When we need that data to enact a transaction or to provide you
                with services and products that you request. This includes
                personalizing features and protecting the security of{' '}
                {config.appName} and its users.
              </li>
              <li>
                When {config.appName} has a legitimate interest in using that
                data in the normal ways you’d expect, like ensuring{' '}
                {config.appName}’s products run properly, improving and creating
                new products, historical analytics research, promoting{' '}
                {config.appName}, and protecting our legal rights.
              </li>
              <li>
                When we need to process your data to comply with a legal or
                regulatory obligation.
              </li>
            </ul>

            <p>
              Your rights: You have the following rights with regard to your
              personal data. To exercise any of these rights, please contact us
              at
              <Obfuscate email={emails.privacy} />. Please note that these
              rights are limited, like for example, where fulfilling your
              request would adversely affect other individuals or our legal
              obligations.
            </p>
            <ul>
              <li>
                Right to Access: You have the right to request information about
                your personal data that we hold, how we use it, and who we share
                it with.
              </li>
              <li>
                Right to Portability: Where you have provided your personal data
                to us on the basis of your consent, you have the right to ask us
                for a copy of this data in a structured, machine-readable format
                and to ask us to send this data to another data controller.
              </li>
              <li>
                Right to Rectification: You have the right to ask us to correct
                your personal data where it is inaccurate or incomplete.
              </li>
              <li>
                Right to Erasure: In certain circumstances, you have the right
                to ask us to delete the personal data we hold about you.
              </li>
              <li>
                Right to Withdraw Consent: In situations where we are processing
                your personal data based on your consent, you may withdraw this
                consent at any time.
              </li>
              <li>
                Right to Object to Processing: In situations where we are
                processing your personal data based on our legitimate interest,
                you can object that the interest is no longer legitimate.{' '}
                {config.appName} will stop processing that data, unless we can
                demonstrate an overriding legitimate ground. You can also
                request to opt out of direct marketing.
              </li>
              <li>
                Right to Restrict Processing: You have the right to ask us to
                stop any active processing of your personal data while we seek
                to verify data you claim is inaccurate, while we verify our
                legitimate interests, or while we cannot erase that data due to
                legal obligations. {config.appName} will try to respond to these
                requests within 30 days, but some might take longer. You will
                typically not be charged any fee for effecting these rights, but{' '}
                {config.appName} reserves the right to charge a reasonable fee
                (or refuse to comply) if the request is unfounded, repetitive,
                or excessive. In the event that you wish to make a complaint
                about how we process your personal data, please contact us at{' '}
                <Obfuscate email={emails.privacy} /> and we will do our best to
                resolve it. You can also choose to file a complaint with the
                relevant data protection authority.
              </li>
            </ul>

            <h3>California residents</h3>
            <p>
              If you live in California and use {config.appName}, you have some
              additional rights when it comes to your data.
            </p>
            <ul>
              <li>
                Right to Delete: You can ask us to delete the personal
                information we have about you.
              </li>
              <li>
                Right to Know: You can ask us for a list of the categories of
                personal information we have about you, the categories of
                sources from which we get personal information, the business
                purposes for which we collect personal information, and the
                categories of third parties with whom it is shared (you can also
                see that in this Privacy Policy). You also can ask us for a copy
                of the pieces of personal information we have about you.
                California law gives you the right to opt out of the sale of
                your personal information. To exercise any of these rights, you
                (or your authorized agent) can contact us by email at{' '}
                <Obfuscate email={emails.privacy} />. We will confirm receipt of
                your request within 10 days, and will provide a response to you
                within 45 days of your request, though in some exceptional cases
                it might take longer (if that happens, we’ll let you know). We
                will need your name and email address to verify your request,
                and may also ask for additional information if necessary to
                verify the identity of the person making the request. We reserve
                the right to deny your request if we cannot verify your
                identity, or if an exemption applies (for example, where
                fulfilling your request would adversely affect other
                individuals, or where we have a conflicting legal obligation).
                That said, we will not discriminate against you for exercising
                any of your privacy rights. To learn about the categories of
                personal information that {config.appName} uses, please see the
                section of this policy called &ldquo;What data we collect and
                why.&rdquo; The personal information that we have collected in
                the past 12 months falls into the following categories as set
                out under California law:
                <ul>
                  <li>
                    The contact information you provide – California law refers
                    to these as identifiers.
                  </li>
                  <li>
                    We don’t track your location, but your IP address gives us a
                    general idea of the city, state, and country - what
                    California law calls geolocation.
                  </li>
                </ul>
              </li>
              <li>
                Please note that, while {config.appName} provides tools to
                manage your privacy as described in this policy, we do not
                support &ldquo;Do Not Track&rdquo; browser settings at this
                time. Lastly, California residents also have the right to
                request this information in an accessible alternative format. If
                you have a disability and would like to access this privacy
                policy in an alternative format, please contact us by email or
                mail as detailed below.
              </li>
            </ul>
          </div>
        ) : null}

        <h3>Minors</h3>
        <p>
          We created {config.appName} for the exclusive use of adults (18 and
          older). We don’t knowingly collect or solicit personal information
          from children. If you are a child under 18, please do not attempt to
          use or register for {config.appName}’s products or send any personal
          information to us. If we learn we have collected personal information
          from someone under 18, we will delete that information as quickly as
          possible. If you are a parent or guardian of a child who you think may
          have given us some personal information or posted information on any
          public portion of {config.appName}
          products, please let us know at <Obfuscate email={emails.privacy} />.
          We’ll help you remove it.
        </p>

        <h3>Changes to this policy</h3>
        <p>
          We will continue to update our policies and practices as needed. We
          will notify you of any changes to our Privacy Policy by posting any
          changes here. If we do, you’ll see that the date at the top of this
          Privacy Policy has changed.
        </p>

        <p>How to contact us</p>
        <p>
          If you have any questions about our privacy policies and practice,
          please contact us at <Obfuscate email={emails.privacy} />.
        </p>
      </div>
    </div>
  )
}

Privacy.title = `${config.appName} privacy policy`
Privacy.subtitle = 'Last updated: August 30, 2021'

export default Privacy
